import React from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Textarea } from '../../ui/textarea';
import { cn } from '../../ui/utils';
import type { PerformanceScheduleDetail } from '../../../types/performance.types';
import { performanceApi } from '../../../utils/performance-api';
import { PerformancePill } from '../PerformancePrimitives';

interface ReviewTabProps {
  detail: PerformanceScheduleDetail;
  onChanged: () => Promise<void> | void;
}

export const ReviewTab: React.FC<ReviewTabProps> = ({ detail, onChanged }) => {
  const schedule = detail.schedule;
  const [mid, setMid] = React.useState(schedule.mid);
  const [eoy, setEoy] = React.useState(schedule.eoy);
  const [levels, setLevels] = React.useState(detail.levels);
  const [saving, setSaving] = React.useState(false);
  const [savingScale, setSavingScale] = React.useState(false);

  React.useEffect(() => {
    setMid(schedule.mid);
    setEoy(schedule.eoy);
  }, [schedule]);
  React.useEffect(() => {
    setLevels(detail.levels);
  }, [detail.levels]);

  const readOnly = schedule.status === 'Closed';
  const scaleLocked = detail.results.some((result) => result.selfScore != null);

  const saveWindows = async () => {
    setSaving(true);
    try {
      await performanceApi.schedules.update(schedule.id, {
        midEnabled: mid.enabled,
        midStart: mid.start,
        midEnd: mid.end,
        midSelfStart: mid.selfStart,
        midSelfEnd: mid.selfEnd,
        midManagerStart: mid.managerStart,
        midManagerEnd: mid.managerEnd,
        eoyEnabled: eoy.enabled,
        eoyStart: eoy.start,
        eoyEnd: eoy.end,
        eoySelfStart: eoy.selfStart,
        eoySelfEnd: eoy.selfEnd,
        eoyManagerStart: eoy.managerStart,
        eoyManagerEnd: eoy.managerEnd,
      });
      toast.success('Review windows saved');
      await onChanged();
    } finally {
      setSaving(false);
    }
  };

  const saveScale = async () => {
    setSavingScale(true);
    try {
      for (const level of levels) {
        const original = detail.levels.find((row) => row.score === level.score);
        if (
          original &&
          (original.label !== level.label || (original.definition || '') !== (level.definition || ''))
        ) {
          await performanceApi.settings.updateLevel(schedule.id, level.score, {
            label: level.label,
            definition: level.definition,
          });
        }
      }
      toast.success('Rating scale saved');
      await onChanged();
    } finally {
      setSavingScale(false);
    }
  };

  const windowFields = (
    kind: 'mid' | 'eoy',
    value: typeof mid,
    setValue: React.Dispatch<React.SetStateAction<typeof mid>>
  ) => (
    <div className="mt-3 grid gap-3 sm:grid-cols-2">
      {(
        [
          ['start', 'Review opens'],
          ['end', 'Review closes'],
          ['selfStart', 'Self-review starts'],
          ['selfEnd', 'Self-review ends'],
          ['managerStart', 'Manager review starts'],
          ['managerEnd', 'Manager review ends'],
        ] as const
      ).map(([key, label]) => (
        <div key={key} className="space-y-1.5">
          <Label htmlFor={`${kind}-${key}`}>{label}</Label>
          <Input
            id={`${kind}-${key}`}
            type="date"
            value={value[key] || ''}
            min={schedule.startDate}
            max={schedule.endDate}
            onChange={(event) =>
              setValue((current) => ({ ...current, [key]: event.target.value }))
            }
          />
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold">Mid-year review</h3>
            <p className="text-xs text-muted-foreground">
              Switching it off removes the tab for everyone.
            </p>
          </div>
          <Switch
            checked={mid.enabled}
            disabled={readOnly}
            onCheckedChange={(checked) =>
              setMid((current) => ({ ...current, enabled: checked }))
            }
          />
        </div>
        {mid.enabled ? windowFields('mid', mid, setMid) : null}
      </section>

      <section className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold">End-of-year review</h3>
            <p className="text-xs text-muted-foreground">
              Calibration opens after this window; results release on Close.
            </p>
          </div>
          <Switch
            checked={eoy.enabled}
            disabled={readOnly}
            onCheckedChange={(checked) =>
              setEoy((current) => ({ ...current, enabled: checked }))
            }
          />
        </div>
        {eoy.enabled ? windowFields('eoy', eoy, setEoy) : null}
      </section>

      {!readOnly ? (
        <Button onClick={saveWindows} disabled={saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save review windows
        </Button>
      ) : null}

      <section className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold">Rating scale</h3>
            <p className="text-xs text-muted-foreground">
              {scaleLocked
                ? 'Locked — a self-review has already been submitted against this scale.'
                : 'Five levels, lowest to highest. Definitions appear under each rating.'}
            </p>
          </div>
          <PerformancePill tone={scaleLocked ? 'warning' : 'success'}>
            {scaleLocked ? 'Locked' : 'Editable'}
          </PerformancePill>
        </div>

        <div className="mt-3 space-y-2">
          {[...levels]
            .sort((a, b) => b.score - a.score)
            .map((level) => (
              <div key={level.score} className="rounded-[10px] border border-border p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={cn(
                      'grid h-7 w-7 shrink-0 place-items-center rounded-full border text-xs font-semibold',
                      level.tone === 'success' && 'border-success-border bg-success-bg text-success',
                      level.tone === 'warning' && 'border-warning-border bg-warning-bg text-warning',
                      level.tone === 'danger' && 'border-destructive-border bg-destructive-bg text-destructive',
                      level.tone === 'blue' && 'border-link/30 bg-selection text-link'
                    )}
                  >
                    {level.score}
                  </span>
                  <Input
                    value={level.label}
                    disabled={scaleLocked || readOnly}
                    onChange={(event) =>
                      setLevels((current) =>
                        current.map((row) =>
                          row.score === level.score
                            ? { ...row, label: event.target.value }
                            : row
                        )
                      )
                    }
                    className="max-w-md"
                  />
                </div>
                <Textarea
                  value={level.definition || ''}
                  disabled={scaleLocked || readOnly}
                  onChange={(event) =>
                    setLevels((current) =>
                      current.map((row) =>
                        row.score === level.score
                          ? { ...row, definition: event.target.value }
                          : row
                      )
                    )
                  }
                  rows={2}
                  className="mt-2"
                  placeholder="What this rating means"
                />
              </div>
            ))}
        </div>

        {!scaleLocked && !readOnly ? (
          <Button className="mt-3" variant="outline" onClick={saveScale} disabled={savingScale}>
            {savingScale ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save rating scale
          </Button>
        ) : null}
      </section>
    </div>
  );
};
